import {
  CircularProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  Button,
  Tooltip
} from "@mui/material";
import { ReactComponent as ExcelSvg } from "assets/icon_excel.svg";
import { ReactComponent as PdfSvg } from "assets/icon_pdf.svg";
import { ReactComponent as LocationsPinSvg } from "assets/pin_locations.svg";
import Icon from "components/icon/icon";
import Results from "components/results/results";
import { LeftPanel, RightPanel } from "helpers/constants";
import { AppAction, AppContext, setLeftPanel } from "helpers/context";
import { handleFilterLocations } from "helpers/helpers";
import { DrawShapeSource } from "helpers/models";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import generate from "report/generation";
import getDoc from "report/set/document";
import { downloadExcel } from "../download/download-analysis";
import styles from "./multi-locations.module.scss";
import SelectAggregatioModal from "./selectAggregatioModal";
import { handleGetMultipleResultGroupData } from "helpers/groupResultData";

function MultiLocations() {
  const [context, dispatch] = useContext(AppContext);
  const locationSetAnalysed = context.multipleLocationsAnalysed;
  const [t] = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const locationsTable = handleFilterLocations({
    data: context.currentMultipleLocations?.items || [],
    unit:
      context.currentMultipleLocations?.aggregationUnit ?? "numberOfLocations",
    multipleLocationsSearch: context.multipleLocationsSearch,
    locationsFilterInfo: context.locationsFilterInfo,
    filterChecked: true
  });
  const [selectAggregatioModalOpen, setSelectAggregatioModalOpen] =
    useState(false);
  const [aggregationUnits, setAggregationUnits] = useState<string[]>([]);
  const currentShapeSource = (context.currentShape as any)?.source;
  const hasShapeContext =
    currentShapeSource === DrawShapeSource.LINE ||
    currentShapeSource === DrawShapeSource.SHAPE_LINE ||
    currentShapeSource === DrawShapeSource.POLYGON ||
    currentShapeSource === DrawShapeSource.SHAPE_POLYGON;
  const canBackToShapePanel = context.lineAnalysisPanelVisible && hasShapeContext;

  const getTargetShapePanel = () =>
    currentShapeSource === DrawShapeSource.POLYGON ||
    currentShapeSource === DrawShapeSource.SHAPE_POLYGON
      ? RightPanel.Polygon
      : RightPanel.Line;

  const handleGenerate = (data: string[]) => {
    setSelectAggregatioModalOpen(false);
    handleDownloadPdf(data);
    setAggregationUnits(data);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadPdf = (units?: string[]) => {
    if (isLoading) return;
    setAnchorEl(null);

    const locationSet: any = { ...context.currentMultipleLocations };
    if (!locationSet) {
      return;
    }

    // ✅ 修复：在handleDownloadPdf内部重新计算locationsTable，确保使用最新的context数据
    const freshLocationsTable = handleFilterLocations({
      data: context.currentMultipleLocations?.items || [],
      unit:
        context.currentMultipleLocations?.aggregationUnit ??
        "numberOfLocations",
      multipleLocationsSearch: context.multipleLocationsSearch,
      locationsFilterInfo: context.locationsFilterInfo,
      filterChecked: true
    });

    if (!freshLocationsTable?.length) {
      return;
    }

    locationSet.items = freshLocationsTable;

    // if (!locationsTable?.length) {
    //   return;
    // }

    // locationSet.items = locationsTable;

    const currency = locationSet?.currency || "";

    let analysisItems = [];
    if (
      context.hazardMultipleAnalysisStore &&
      context.hazardMultipleAnalysedLegends
    ) {
      const { pdfResult } = handleGetMultipleResultGroupData({
        storeData: context.hazardMultipleAnalysisStore,
        locationsTableData: freshLocationsTable,
        legendData: context.hazardMultipleAnalysedLegends,
        needNoData: false,
        needChartData: false
      });
      analysisItems = pdfResult;
    }
    const legendItems = context.hazardMultipleAnalysedLegends;

    let eventResult = [];
    if (context.eventAnalysisStore && context.eventAnalysisLegend) {
      const { pdfResult } = handleGetMultipleResultGroupData({
        storeData: context.eventAnalysisStore,
        locationsTableData: freshLocationsTable,
        legendData: context.eventAnalysisLegend,
        needNoData: false,
        needChartData: false
      });
      eventResult = pdfResult;
    }
    const eventLegend = context.eventAnalysisLegend;

    if (
      (!analysisItems.length || !legendItems) &&
      (!eventResult.length || !eventLegend)
    ) {
      return;
    }

    setIsLoading(true);
    getDoc({
      t,
      locationSet,
      analysisItems: analysisItems || [],
      legendItems: legendItems || [],
      eventResult: eventResult || [],
      eventLegend: eventLegend || [],
      currency,
      aggregationUnits: units || aggregationUnits,
      unit:
        context.currentMultipleLocations?.aggregationUnit ??
        "numberOfLocations",
      // unit: "numberOfLocations",
      locationsFilterInfo: context.locationsFilterInfo
    })
      .then((dd) => {
        generate(dd, locationSet.name)
          .then(() => {
            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "reportGenerated",
                message: "setReportGeneratedSuccess"
              }
            });
          })
          .catch(() => {
            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "downloadPdfTitle",
                titleIconColor: "yellow",
                titleIcon: "warning",
                resMessage: t("snackBar.setReportGeneratedFailed")
              }
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch(() => {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "downloadPdfTitle",
            titleIconColor: "yellow",
            titleIcon: "warning",
            resMessage: t("snackBar.downloadPdfMessage")
          }
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const openModal = (): void => {
    dispatch({
      type: AppAction.setMultipleAnalyseModal,
      payload: { type: "2" }
    });
  };

  const closeLocations = (): void => {
    dispatch({ type: AppAction.setJobId, payload: undefined });
    // 仅当线分析面板在打开地点集前处于可见状态时，关闭地点集才回到线分析面板
    if (canBackToShapePanel) {
      dispatch({ type: AppAction.setRightPanel, payload: getTargetShapePanel() });
    } else {
      dispatch({ type: AppAction.setLineAnalysisPanelVisible, payload: false });
      dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Empty });
    }
    // dispatch({ type: AppAction.setLeftPanel, payload: LeftPanel.Empty });
    dispatch({ type: AppAction.setMultipleLocationsEdit, payload: false });
    dispatch({
      type: AppAction.setCurrentMultipleLocations,
      payload: undefined
    });
    dispatch({ type: AppAction.setMapCenter, payload: undefined });
    dispatch({ type: AppAction.setSearchLocationInfo, payload: undefined });
    dispatch({ type: AppAction.setSingleLocationAnalysed, payload: false });
  };

  function getDate() {
    const today = new Date();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    const date = today.getDate();
    return `${date}/${month}/${year}`;
  }

  const handleDownloadExcel = async () => {
    setIsLoading(true);
    setAnchorEl(null);
    downloadExcel(context, t).finally(() => {
      setIsLoading(false);
    });
  };

  const addLocation = () => {
    setLeftPanel(dispatch, LeftPanel.AddLocation);
  };

  const getAggregationByValue = () => {
    if (
      context.currentMultipleLocationsAnalysed?.id ===
      context.currentMultipleLocations?.id
    ) {
      return t(
        "locations." +
          context.currentMultipleLocationsAnalysed.aggregationUnit ||
          "numberOfLocations"
      );
    } else {
      return t(
        "locations." + context.currentMultipleLocations?.aggregationUnit ||
          "numberOfLocations"
      );
    }
  };

  return (
    <div className={styles.multiple}>
      <div className="overflow-auto">
        <div className={styles.title}>
          <div className="truncate">{context.currentMultipleLocations?.name}</div>
          <div className="flex items-center">
            {/* <div
							className="tooltip z-50"
							data-tip={t("single.addLocation")}
						>
							<div className={styles.icon}>
								<AddLocationSvg onClick={addLocation} />
							</div>
						</div> */}
            <div>
              <Icon onClick={closeLocations} />
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-items-start p-[16px]">
          <div className={styles.image}>
            <LocationsPinSvg />
          </div>
          <div className={styles.address}>
            <div>
              <div style={{ display: "flex" }}>
                <div style={{ marginRight: "5px" }}>
                  {t("multiple.aggregationBy")}
                </div>
                <div>{getAggregationByValue()}</div>
              </div>
              <div>
                {t("multiple.createTime")}:{" "}
                {context.currentMultipleLocations?.createTime}
              </div>
              {locationSetAnalysed && (
                <div>
                  {t("multiple.analysisDate")}: {getDate()}
                </div>
              )}
              {/* <Button>{t("multiple.details")}</Button> */}
            </div>
          </div>
        </div>
        {locationSetAnalysed ? (
          <Results />
        ) : (
          <div className={styles.locationCount}>
            {locationsTable.length}&nbsp;{t("multiple.locations")}
          </div>
        )}
      </div>
      <div className={styles.buttons}>
        {locationSetAnalysed && (
          <div className="w-fit mr-3">
            {/* <a
							href="/CatNet_Report_Multiple_Premium.pdf"
							target="_blank"
							rel="noreferrer"
						>
							<Button type="outlined">{t("buttons.download")}</Button>
						</a> */}
            <Button
              color="success"
              variant="outlined"
              onClick={handleClick}
              endIcon={
                isLoading ? (
                  <CircularProgress color="success" size={24} />
                ) : (
                  <div className="material-icons">keyboard_arrow_down</div>
                )
              }
              disabled={isLoading}
            >
              {t("buttons.download")}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center"
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center"
              }}
              MenuListProps={{
                "aria-labelledby": "basic-button"
              }}
              PaperProps={{
                style: {
                  padding: "6px 0",
                  marginTop: -6
                }
              }}
            >
              {context.option !== "climate" && (
                <MenuItem onClick={() => setSelectAggregatioModalOpen(true)}>
                  <ListItemIcon>
                    <PdfSvg className="w-[24px] h-[24px] fill-[#0009] mx-[5px] mt-[-5px]" />
                  </ListItemIcon>
                  <Typography variant="inherit">
                    {t("buttons.PDF Report")}
                  </Typography>
                </MenuItem>
              )}
              <MenuItem onClick={handleDownloadExcel}>
                <ListItemIcon>
                  <ExcelSvg className="w-[24px] h-[24px] fill-[#0009] mx-[5px] mt-[-5px]" />
                </ListItemIcon>
                <Typography variant="inherit">
                  {t("buttons.Excel Sheet")}
                </Typography>
              </MenuItem>
            </Menu>
          </div>
        )}
        <Tooltip
          title={t("buttons.noLocationsSelected")}
          placement="top"
          disableHoverListener={!!locationsTable.length}
        >
          <div className="flex-1">
            <Button
              onClick={openModal}
              disabled={!locationsTable.length}
              variant="contained"
              color="success"
              style={{ width: "100%" }}
            >
              {t("buttons.analyseSelected")}
            </Button>
          </div>
        </Tooltip>
      </div>
      {selectAggregatioModalOpen && (
        <SelectAggregatioModal
          open={selectAggregatioModalOpen}
          handleClose={() => setSelectAggregatioModalOpen(false)}
          handleGenerate={handleGenerate}
        />
      )}
    </div>
  );
}

export default MultiLocations;
